import React, { useState, useEffect } from 'react';
import '../../css/sectorForm.css';

const SectorForm = ({ onSubmit, sector, brigades, circunscripciones, isOpen, onClose, isEditing }) => {
    const [formData, setFormData] = useState({
        name: '',
        url: '',
        status: 'Pendiente',
        workType: 'mixed',
        assignedBrigade: '',
        circunscripcion: '',
        scheduledDate: '',
        coordinates: []
    });
    const [errors, setErrors] = useState({});
    const [currentPage, setCurrentPage] = useState(0);

    useEffect(() => {
        if (isOpen && sector) {
            // Solo actualizar si hay datos válidos en sector
            if (sector.name || sector._id) {
                // Es un sector existente (edición)
                console.log('Editing sector:', sector.assignedBrigade);
                setFormData({
                    name: sector.name || '',
                    url: sector.url || '',
                    status: sector.status || 'Pendiente',
                    workType: sector.workType || 'mixed',
                    assignedBrigade: sector.assignedBrigade?._id || sector.assignedBrigade || '',
                    circunscripcion: sector.circunscripcion || '',
                    scheduledDate: sector.scheduledDate
                        ? new Date(sector.scheduledDate).toISOString().substring(0, 10)
                        : '',
                    coordinates: sector.coordinates || []
                });
            } else if (sector.coordinates) {
                // Solo tiene coordenadas (nuevo sector desde mapa)
                setFormData(prev => ({
                    ...prev,
                    coordinates: sector.coordinates
                }));
            }
        }
    }, [isOpen, sector]);

    useEffect(() => {
        if (!isOpen) {
            setFormData({
                name: '',
                url: '',
                status: 'Pendiente',
                workType: 'mixed',
                assignedBrigade: '',
                circunscripcion: '',
                scheduledDate: '',
                coordinates: []
            });
            setCurrentPage(0);
            setErrors({});
        }
    }, [isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleAddCoordinate = () => {
        const newCoordinates = [...formData.coordinates, [0, 0]];
        setFormData(prev => ({ ...prev, coordinates: newCoordinates }));
        setCurrentPage(newCoordinates.length - 1);
    };

    const handleCoordinateChange = (index, lng, lat) => {
        const newCoordinates = [...formData.coordinates];
        newCoordinates[index] = [lng, lat];
        setFormData(prev => ({ ...prev, coordinates: newCoordinates }));
    };

    const handleDeleteCoordinate = (index) => {
        if (formData.coordinates.length === 1) {
            alert('Debes mantener al menos una coordenada');
            return;
        }

        const confirmDelete = window.confirm(
            `¿Estás seguro de que deseas eliminar la coordenada ${index + 1}?`
        );

        if (!confirmDelete) return;

        const newCoordinates = formData.coordinates.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, coordinates: newCoordinates }));

        if (currentPage >= newCoordinates.length) {
            setCurrentPage(Math.max(0, newCoordinates.length - 1));
        } else if (currentPage > index) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handlePrevious = () => {
        if (currentPage > 0) setCurrentPage(currentPage - 1);
    };

    const handleNext = () => {
        if (currentPage < formData.coordinates.length - 1) setCurrentPage(currentPage + 1);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'El nombre del sector es requerido';
        } else if (formData.name.trim().length < 3) {
            newErrors.name = 'El nombre debe tener al menos 3 caracteres';
        }

        if (!formData.url.trim()) {
            newErrors.url = 'La URL es requerida';
        }

        if (!formData.circunscripcion) {
            newErrors.circunscripcion = 'Debe seleccionar una circunscripción';
        }

        if (!formData.scheduledDate) {
            newErrors.scheduledDate = 'Debe seleccionar una fecha';
        }

        if (formData.coordinates.length === 0) {
            newErrors.coordinates = 'Debe agregar al menos una coordenada';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        onSubmit(formData);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay-sector" onClick={onClose}>
            <div className="modal-content-sector" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header-sector">
                    <div className="header-icon-sector">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                        </svg>
                    </div>
                    <div>
                        <h3>{isEditing ? 'Editar Sector' : 'Nuevo Sector'}</h3>
                        <p className="modal-subtitle-sector">
                            {isEditing ? 'Modifica los detalles del sector' : 'Crea un nuevo sector y define sus coordenadas'}
                        </p>
                    </div>
                    <button className="modal-close-sector" onClick={onClose}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="sector-form-modern">
                    <div className="form-content-sector">
                        {/* Nombre del Sector */}
                        <div className={`form-group-sector ${errors.name ? 'has-error' : ''}`}>
                            <label htmlFor="sector-name">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                    <polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                                Nombre del Sector
                                <span className="required-sector">*</span>
                            </label>
                            <input
                                id="sector-name"
                                name="name"
                                type="text"
                                placeholder="Ej: Sector Norte, Zona Centro..."
                                value={formData.name}
                                onChange={handleChange}
                                className={errors.name ? 'input-error' : ''}
                                maxLength={100}
                            />
                            {errors.name && (
                                <span className="error-message-sector">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    {errors.name}
                                </span>
                            )}
                            <div className="input-hint-sector">
                                {formData.name.length}/100 caracteres
                            </div>
                        </div>

                        {/* URL */}
                        <div className={`form-group-sector ${errors.url ? 'has-error' : ''}`}>
                            <label htmlFor="sector-url">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                </svg>
                                URL
                                <span className="required-sector">*</span>
                            </label>
                            <input
                                id="sector-url"
                                name="url"
                                type="text"
                                placeholder="https://..."
                                value={formData.url}
                                onChange={handleChange}
                                className={errors.url ? 'input-error' : ''}
                            />
                            {errors.url && (
                                <span className="error-message-sector">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    {errors.url}
                                </span>
                            )}
                        </div>

                        {/* Circunscripción y Fecha */}
                        <div className="form-row-sector">
                            <div className={`form-group-sector ${errors.circunscripcion ? 'has-error' : ''}`}>
                                <label htmlFor="sector-circunscripcion">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="2" y1="12" x2="22" y2="12" />
                                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                    </svg>
                                    Circunscripción
                                    <span className="required-sector">*</span>
                                </label>
                                <select
                                    id="sector-circunscripcion"
                                    name="circunscripcion"
                                    value={formData.circunscripcion}
                                    onChange={handleChange}
                                    className={errors.circunscripcion ? 'input-error' : ''}
                                >
                                    <option value="">Seleccionar...</option>
                                    {circunscripciones.map((circ) => (
                                        <option key={circ._id} value={circ._id}>
                                            {circ.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.circunscripcion && (
                                    <span className="error-message-sector">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="8" x2="12" y2="12" />
                                            <line x1="12" y1="16" x2="12.01" y2="16" />
                                        </svg>
                                        {errors.circunscripcion}
                                    </span>
                                )}
                            </div>

                            <div className={`form-group-sector ${errors.scheduledDate ? 'has-error' : ''}`}>
                                <label htmlFor="sector-date">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                    Fecha Programada
                                    <span className="required-sector">*</span>
                                </label>
                                <input
                                    id="sector-date"
                                    name="scheduledDate"
                                    type="date"
                                    value={formData.scheduledDate}
                                    onChange={handleChange}
                                    className={errors.scheduledDate ? 'input-error' : ''}
                                />
                                {errors.scheduledDate && (
                                    <span className="error-message-sector">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="8" x2="12" y2="12" />
                                            <line x1="12" y1="16" x2="12.01" y2="16" />
                                        </svg>
                                        {errors.scheduledDate}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Estado y Brigada */}
                        <div className="form-row-sector">
                            <div className="form-group-sector">
                                <label htmlFor="sector-status">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                    </svg>
                                    Estado
                                </label>
                                <select
                                    id="sector-status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                >
                                    <option value="Pendiente">Pendiente</option>
                                    <option value="Proceso">En Proceso</option>
                                    <option value="Listo">Completado</option>
                                </select>
                            </div>

                            <div className="form-group-sector">
                                <label htmlFor="sector-brigade">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                    Brigada Asignada
                                </label>
                                <select
                                    id="sector-brigade"
                                    name="assignedBrigade"
                                    value={formData.assignedBrigade}
                                    onChange={handleChange}
                                >
                                    <option value="">Sin asignar</option>
                                    {brigades.map((brigade) => (
                                        <option key={brigade._id} value={brigade._id}>
                                            {brigade.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Tipo de Trabajo */}
                        <div className="form-group-sector">
                            <label htmlFor="sector-workType">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                                </svg>
                                Tipo de Trabajo
                            </label>
                            <select
                                id="sector-workType"
                                name="workType"
                                value={formData.workType}
                                onChange={handleChange}
                            >
                                <option value="mixed">Mixto</option>
                                <option value="trimmer">Corte / Mantenimiento de Cesped</option>
                                <option value="tree">Poda / Corte / Siembra de Arboles</option>
                                <option value="ornamental">Mantenimiento de Ornato (Arbustos)</option>
                            </select>
                        </div>

                        {/* Coordenadas */}
                        <div className="coordinates-section-sector">
                            <div className="coordinates-header-sector">
                                <div className="coord-title-sector">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    <h4>Coordenadas ({formData.coordinates.length})</h4>
                                </div>
                                <button
                                    type="button"
                                    className="btn-add-coordinate-sector"
                                    onClick={handleAddCoordinate}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Agregar
                                </button>
                            </div>

                            {errors.coordinates && (
                                <span className="error-message-sector">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    {errors.coordinates}
                                </span>
                            )}

                            {formData.coordinates.length > 0 ? (
                                <div className="coordinate-card-sector">
                                    <div className="coordinate-card-header-sector">
                                        <div className="coordinate-number-sector">
                                            <span className="coord-badge-sector">
                                                Coordenada {currentPage + 1} de {formData.coordinates.length}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-delete-coordinate-sector"
                                            onClick={() => handleDeleteCoordinate(currentPage)}
                                            title="Eliminar esta coordenada"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                <line x1="10" y1="11" x2="10" y2="17" />
                                                <line x1="14" y1="11" x2="14" y2="17" />
                                            </svg>
                                        </button>
                                    </div>

                                    <div className="coordinate-inputs-sector">
                                        <div className="form-group-sector">
                                            <label htmlFor="longitude">
                                                Longitud
                                            </label>
                                            <input
                                                id="longitude"
                                                type="number"
                                                step="0.000001"
                                                placeholder="Ej: -69.931212"
                                                value={formData.coordinates[currentPage][0]}
                                                onChange={(e) => handleCoordinateChange(
                                                    currentPage,
                                                    parseFloat(e.target.value) || 0,
                                                    formData.coordinates[currentPage][1]
                                                )}
                                            />
                                        </div>
                                        <div className="form-group-sector">
                                            <label htmlFor="latitude">
                                                Latitud
                                            </label>
                                            <input
                                                id="latitude"
                                                type="number"
                                                step="0.000001"
                                                placeholder="Ej: 18.486058"
                                                value={formData.coordinates[currentPage][1]}
                                                onChange={(e) => handleCoordinateChange(
                                                    currentPage,
                                                    formData.coordinates[currentPage][0],
                                                    parseFloat(e.target.value) || 0
                                                )}
                                            />
                                        </div>
                                    </div>

                                    {formData.coordinates.length > 1 && (
                                        <div className="pagination-controls-sector">
                                            <button
                                                type="button"
                                                className="btn-paginate-sector"
                                                onClick={handlePrevious}
                                                disabled={currentPage === 0}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="15 18 9 12 15 6" />
                                                </svg>
                                                Anterior
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-paginate-sector"
                                                onClick={handleNext}
                                                disabled={currentPage === formData.coordinates.length - 1}
                                            >
                                                Siguiente
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="9 18 15 12 9 6" />
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="no-coordinates-sector">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    <p>No hay coordenadas</p>
                                    <p className="no-coordinates-hint-sector">Agrega coordenadas haciendo clic en el botón de arriba</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-actions-sector">
                        <button type="button" className="btn-cancel-sector" onClick={onClose}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                            Cancelar
                        </button>
                        <button type="submit" className="btn-submit-sector">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            {isEditing ? 'Actualizar' : 'Guardar'} Sector
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SectorForm;